import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { categoryById, isSensitive, EXPIRY_WARN_DAYS } from "@/lib/categories";
import UpcomingExpiry from "@/components/vault/UpcomingExpiry";
import SecretFormModal from "@/components/vault/SecretFormModal";
import SecretDetailDrawer from "@/components/vault/SecretDetailDrawer";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Search,
  Star,
  Copy,
  Eye,
  EyeOff,
  Shield,
  AlertTriangle,
  Mail,
  Server,
  ShieldCheck,
  Cpu,
  HardDrive,
  Globe,
  Key,
  Lock,
  Cloud,
} from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = { Mail, Server, ShieldCheck, Cpu, HardDrive, Globe, Key, Lock, Cloud };

const PRIMARY_FIELD = {
  email: "email",
  server: "host_ip",
  vpn: "gateway_ip",
  firewall: "management_ip",
  idrac: "idrac_ip",
  domain: "domain_name",
  license: "software_name",
  cloud: "provider",
  other: "username",
};

const SECONDARY_FIELD = {
  email: "password",
  server: "password",
  vpn: "password",
  firewall: "admin_password",
  idrac: "admin_password",
  domain: "registrar",
  license: "license_key",
  cloud: "password",
  other: "password",
};

function expiryStatus(secret) {
  const exp = secret.fields?.expiry_date;
  if (!exp) return null;
  const d = new Date(exp);
  if (isNaN(d)) return null;
  const now = new Date();
  const days = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: "Süresi geçti", color: "text-rose-400 bg-rose-500/10 border-rose-500/30" };
  if (days <= (EXPIRY_WARN_DAYS[secret.category] || 30))
    return {
      label: `${days} gün kaldı`,
      color: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    };
  return {
    label: d.toLocaleDateString("tr-TR"),
    color: "text-slate-400 bg-slate-800 border-slate-700",
  };
}

export default function VaultView({ mode }) {
  const params = useParams();
  const { user } = useAuth();
  const categoryId = mode === "category" ? params.categoryId : null;
  const favoritesOnly = mode === "favorites";

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [revealed, setRevealed] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const p = {};
      if (categoryId) p.category = categoryId;
      if (favoritesOnly) p.favorites = true;
      if (search) p.q = search;
      const { data } = await api.get("/secrets", { params: p });
      setItems(data);
    } catch (e) {
      toast.error("Kayıtlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, favoritesOnly]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const open = () => {
      setEditTarget(null);
      setFormOpen(true);
    };
    const refresh = () => load();
    window.addEventListener("vaultops:open-create", open);
    window.addEventListener("vaultops:refresh", refresh);
    return () => {
      window.removeEventListener("vaultops:open-create", open);
      window.removeEventListener("vaultops:refresh", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = useMemo(() => {
    if (favoritesOnly) return "Favoriler";
    if (categoryId) return categoryById(categoryId).label;
    return "Tüm Kayıtlar";
  }, [categoryId, favoritesOnly]);

  const subtitle = useMemo(() => {
    if (favoritesOnly) return "Hızlı erişim için yıldızladığınız kayıtlar";
    if (categoryId) return categoryById(categoryId).labelEn;
    return "Ekibinize ait tüm gizli bilgiler, tek bir yerde";
  }, [categoryId, favoritesOnly]);

  const toggleFav = async (e, item) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/secrets/${item.secret_id}/favorite`);
      const favBy = new Set(item.favorite_by || []);
      if (data.favorite) favBy.add(user.user_id);
      else favBy.delete(user.user_id);
      setItems((arr) =>
        arr.map((x) =>
          x.secret_id === item.secret_id
            ? { ...x, favorite_by: Array.from(favBy) }
            : x
        )
      );
      window.dispatchEvent(new CustomEvent("vaultops:refresh"));
    } catch {}
  };

  const toggleReveal = async (e, item) => {
    e.stopPropagation();
    const key = item.secret_id;
    const show = !revealed[key];
    setRevealed((r) => ({ ...r, [key]: show }));
    if (show) {
      try {
        await api.post(`/secrets/${item.secret_id}/reveal`);
      } catch {}
      setTimeout(() => setRevealed((r) => ({ ...r, [key]: false })), 10000);
    }
  };

  const copyPassword = async (e, item) => {
    e.stopPropagation();
    const sec = SECONDARY_FIELD[item.category];
    const val = item.fields?.[sec];
    if (!val) {
      toast.error("Kopyalanacak değer yok");
      return;
    }
    await navigator.clipboard.writeText(String(val));
    toast.success("Parola kopyalandı");
    try {
      await api.post(`/secrets/${item.secret_id}/reveal`);
    } catch {}
  };

  const openDetail = (item) => {
    setSelected(item);
    setDrawerOpen(true);
  };
  const openEdit = (item) => {
    setEditTarget(item);
    setDrawerOpen(false);
    setFormOpen(true);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-1">
            {mode === "category" ? "Kategori" : favoritesOnly ? "Filtre" : "Vault"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-50">
            {title}
          </h1>
          <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
        </div>
        <div className="relative max-w-md w-full">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="global-search-input"
            placeholder="Ara: başlık, host, kullanıcı, etiket..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-11 bg-[#111726] border-slate-800 text-slate-100 font-mono text-sm"
          />
        </div>
      </div>

      {mode === "all" && <UpcomingExpiry />}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-xl bg-[#111726] border border-slate-800 animate-pulse"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="p-12 rounded-2xl bg-[#111726] border border-slate-800 text-center">
          <Lock className="w-10 h-10 mx-auto text-slate-600 mb-4" />
          <div className="text-slate-300 font-medium">Henüz kayıt yok</div>
          <div className="text-sm text-slate-500 mt-1">
            “Yeni Kayıt” butonu ile ilk gizli bilginizi ekleyin.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="secret-grid">
          {items.map((item) => {
            const cat = categoryById(item.category);
            const Icon = ICON_MAP[cat.icon] || Lock;
            const primaryKey = PRIMARY_FIELD[item.category];
            const primary = item.fields?.[primaryKey];
            const exp = expiryStatus(item);
            const favActive = (item.favorite_by || []).includes(user?.user_id);
            const show = revealed[item.secret_id];
            const secKey = SECONDARY_FIELD[item.category];
            const secVal = item.fields?.[secKey];
            const sensitive = isSensitive(secKey);
            return (
              <div
                key={item.secret_id}
                onClick={() => openDetail(item)}
                data-testid={`secret-card-${item.secret_id}`}
                className="group cursor-pointer p-5 rounded-xl bg-[#111726] border border-slate-800 hover:border-emerald-500/40 transition-all duration-200 shadow-lg hover:shadow-emerald-500/5"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center group-hover:border-emerald-500/30 group-hover:bg-emerald-500/10 transition-colors flex-shrink-0">
                      <Icon className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-100 truncate">
                        {item.title}
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                        {cat.labelEn}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => toggleFav(e, item)}
                    data-testid={`fav-toggle-${item.secret_id}`}
                    className={`p-1.5 rounded transition-colors ${
                      favActive
                        ? "text-amber-400"
                        : "text-slate-600 hover:text-amber-400"
                    }`}
                  >
                    <Star className="w-4 h-4" fill={favActive ? "currentColor" : "none"} />
                  </button>
                </div>

                {primary && (
                  <div className="font-mono text-xs text-slate-400 break-all mb-2">
                    {primary}
                  </div>
                )}

                {secVal && (
                  <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-slate-900/60 border border-slate-800">
                    <div
                      className={`font-mono text-sm truncate ${
                        sensitive && !show ? "secret-dots" : "text-emerald-400"
                      }`}
                    >
                      {sensitive && !show ? "••••••••••••" : String(secVal)}
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {sensitive && (
                        <button
                          onClick={(e) => toggleReveal(e, item)}
                          data-testid={`secret-card-reveal-password-button-${item.secret_id}`}
                          className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-400"
                        >
                          {show ? (
                            <EyeOff className="w-3.5 h-3.5" />
                          ) : (
                            <Eye className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={(e) => copyPassword(e, item)}
                        data-testid={`secret-card-copy-password-button-${item.secret_id}`}
                        className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-400"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {item.admin_only && (
                      <Badge className="bg-rose-500/10 border border-rose-500/30 text-rose-400 font-mono text-[10px] uppercase tracking-widest">
                        <Shield className="w-3 h-3 mr-1" />
                        Admin
                      </Badge>
                    )}
                    {exp && (
                      <Badge
                        variant="outline"
                        className={`font-mono text-[10px] uppercase tracking-widest ${exp.color}`}
                      >
                        {exp.label.includes("geçti") && (
                          <AlertTriangle className="w-3 h-3 mr-1" />
                        )}
                        {exp.label}
                      </Badge>
                    )}
                    {(item.tags || []).slice(0, 2).map((t) => (
                      <Badge
                        key={t}
                        variant="secondary"
                        className="bg-slate-800 text-slate-400 border border-slate-700 font-mono text-[10px]"
                      >
                        {t}
                      </Badge>
                    ))}
                  </div>
                  <div className="text-[10px] font-mono text-slate-500">
                    {new Date(item.updated_at).toLocaleDateString("tr-TR")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <SecretDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        secret={selected}
        onEdit={openEdit}
      />
      <SecretFormModal
        open={formOpen}
        onOpenChange={(v) => {
          setFormOpen(v);
          if (!v) setEditTarget(null);
        }}
        initial={editTarget}
        defaultCategory={categoryId}
      />
    </div>
  );
}
